// src/contexts/AuthContext.tsx - Simplified token validation
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { auth } from '../firebase';
import { onAuthStateChanged, User as FirebaseUser, signOut } from 'firebase/auth';
import { UserRole, getUserProfile } from '../services/user.service';
import { clearAuthData } from '../utils/tokenValidation';


interface AuthContextType {
  user: FirebaseUser | null;
  profile: any | null;
  role: UserRole | null;
  loading: boolean;
  authenticated: boolean;
  hasProfile: boolean;
  logout: () => Promise<void>;
  refreshUserProfile: () => Promise<void>;
  setUserData: (user: FirebaseUser | null, role?: UserRole | null, profile?: any | null) => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  role: null,
  loading: true,
  authenticated: false,
  hasProfile: false,
  logout: async () => {},
  refreshUserProfile: async () => {},
  setUserData: () => {},
});

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [profile, setProfile] = useState<any | null>(null);
  const [role, setRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [sessionValid, setSessionValid] = useState(false);

  // Initialize hasProfile from localStorage immediately
  const [hasProfileState, setHasProfileState] = useState<boolean>(() => {
    const storedHasProfile = localStorage.getItem('hasProfile');
    console.log('🔍 Initializing hasProfile from localStorage:', storedHasProfile);
    return storedHasProfile === 'true';
  });
  
  // Handle token expiration
  const handleTokenExpiration = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out from Firebase:', error);
    }
    
    clearAuthData();
    setUser(null);
    setRole(null);
    setProfile(null);
    setHasProfileState(false);
    setSessionValid(false);

    const publicPaths = ['/', '/login', '/signup', '/verify', '/terms', '/privacy', '/the-gallery'];
    const isPublicPath = publicPaths.some(path => 
      window.location.pathname === path || window.location.pathname.startsWith(path)
    );
    
    if (!isPublicPath) {
      console.log('🔄 Redirecting to login from:', window.location.pathname);
      window.location.href = '/login';
    }
  };

  // Check backend session validity first
  const checkBackendSession = async(): Promise<boolean> => {
    try {
      const authUrl = `${import.meta.env.VITE_AUTH_API_BASE_URL}/auth/validate`;
      console.log('🌐 Fetching URL:', authUrl);
      console.log('🔑 VITE_AUTH_API_BASE_URL:', import.meta.env.VITE_AUTH_API_BASE_URL);
    
      const response = await fetch(authUrl, {
        method: 'GET',
        credentials: 'include', // Important: sends the httpOnly cookie
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        console.log('✅ Backend session is valid');
        return true;
      } else {
        const errorData = await response.json();
        console.log('❌ Backend session invalid:', errorData.reason);
        return false;
      }
    } catch (error: any) {
      console.error('❌ Backend session check failed:', error);
      return false;
    }
  };

  // Load user profile after session is verified
  const loadUserProfile = async(): Promise<void> => {
    try {
      console.log('📄 Loading user profile...');
      const profileData = await getUserProfile();
      
      console.log('✅ Profile loaded');
      setProfile(profileData);
      const profileExists = !!profileData;
      setHasProfileState(profileExists);
      localStorage.setItem('hasProfile', profileExists.toString());
    } catch (error: any) {
      console.error('❌ Failed to load profile:', error);
      setProfile(null);
      setHasProfileState(false);
      localStorage.setItem('hasProfile', 'false');
    }
  };
  
  const setUserData = (user: FirebaseUser | null, role?: UserRole | null, profile?: any | null) => {
    setUser(user);
    
    if (role !== undefined) {
      setRole(role);
      if (role) {
        localStorage.setItem('userRole', role);
      } else {
        localStorage.removeItem('userRole');
      }
    }
    
    if (profile !== undefined) {
      setProfile(profile);
      const profileExists = !!profile;
      setHasProfileState(profileExists);
      localStorage.setItem('hasProfile', profileExists.toString());
    }
    // Mark session as valid when manually setting user data (e.g., after login)
    if (user) {
      setSessionValid(true);
    }
  };
  
  const refreshUserProfile = async () => {
    if (!user) return;
    
    try {
      const profileData = await getUserProfile(); // backend reads cookie
      setProfile(profileData);
      
      const profileExists = !!profileData;
      setHasProfileState(profileExists);
      localStorage.setItem('hasProfile', profileExists.toString());
    } catch (error: any) {
      console.error('Error refreshing user profile:', error);
      // If API call fails with auth error, it might be due to expired token
      if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
        handleTokenExpiration();
      }
    }
  };
  
  const logout = async () => {
    try {
      await signOut(auth);
      clearAuthData();
      setUser(null);
      setRole(null);
      setProfile(null);
      setHasProfileState(false);
      setSessionValid(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };
  
  //  Check backend session BEFORE setting user state ?working ?
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('🎯 onAuthStateChanged fired, user:', firebaseUser?.email || 'none');
    
      if (firebaseUser) {
        console.log('👤 Firebase user detected:', firebaseUser.email);
        
        // ✅ CRITICAL: Check backend session validity FIRST
        console.log('⏳ About to check backend session...');
        const isBackendSessionValid = await checkBackendSession();
        console.log('🎲 Backend session valid?', isBackendSessionValid);

        if (!isBackendSessionValid) {
          console.log('❌ Backend session invalid, clearing everything');
          await handleTokenExpiration();
          setLoading(false);
          setAuthInitialized(true);
          return; // Exit early - don't set user or sessionValid
        }
        
        // ✅ Backend session is valid - NOW we can set user and sessionValid
        console.log('✅ Backend session verified, setting user state');
        setUser(firebaseUser);
        setSessionValid(true); // This makes authenticated = true
        
        // Restore role from localStorage
        const roleStr = localStorage.getItem('userRole');
        if (roleStr) {
          setRole(roleStr as UserRole);
        }
        
        // Check if we have cached hasProfile value
        const hasProfileStr = localStorage.getItem('hasProfile');
        if (hasProfileStr !== null && !authInitialized) {
          const hasProfileBool = hasProfileStr === 'true';
          setHasProfileState(hasProfileBool);
          console.log('🔍 Restored hasProfile from localStorage:', hasProfileBool);
        }
        
        // Load the full profile
        await loadUserProfile();
        
      } else {
        console.log('🚪 No Firebase user, clearing state');
        setUser(null);
        setRole(null);
        setProfile(null);
        setHasProfileState(false);
        setSessionValid(false);
        clearAuthData();
      }
      
      setAuthInitialized(true);
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, [authInitialized]);
  
  const getAuthenticated = (): boolean => {
    if (!user) return false;
    if (!sessionValid) return false;
    return true;
  };
  
  const authenticated = getAuthenticated();
  const hasProfile = hasProfileState;
  
  const value = {
    user,
    profile,
    role,
    loading,
    authenticated,
    hasProfile,
    logout,
    refreshUserProfile,
    setUserData,
  };
  
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};